import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import {
  Printer,
  Download,
  Mail,
  ChevronDown,
  ChevronUp,
  RotateCcw,
} from "lucide-react";
import React, { useState } from "react";
import { usePaymentDetail } from "@features/cx/payments/hooks";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface RefundDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: any;
}

export const RefundDetailsModal: React.FC<RefundDetailsModalProps> = ({
  isOpen,
  onClose,
  payment,
}) => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [isBankTransferExpanded, setIsBankTransferExpanded] = useState(false);
  const { data: paymentData } = usePaymentDetail({
    paymentId: payment?.id,
    remoteUtilityId: payment?.remoteUtilityId,
  });

  console.log("payment", paymentData);

  const transactionId = paymentData?.result?.receiptNo || "N/A";
  const refundId = paymentData?.result?.extraData?.refundId || "N/A";
  const name = paymentData?.result?.consumer?.firstName
    ? `${paymentData?.result?.consumer?.firstName} ${paymentData?.result?.consumer?.lastName}`
    : "N/A";
  const consumerNo = paymentData?.result?.consumer?.consumerNo || "N/A";
  const status = paymentData?.result?.paymentReceivedStatus || "N/A";
  const amount = parseFloat(paymentData?.result?.amount || "0").toFixed(2);

  const paymentSummary =
    paymentData?.result?.paymentSummary ||
    (paymentData?.result as any)?.payment_summary ||
    {};
  const originalPaymentAmount = parseFloat(
    paymentSummary?.totalPayableAmount ??
      paymentSummary?.total_payable_amount ??
      amount ??
      "0",
  );
  const thisRefundAmount = parseFloat(
    paymentSummary?.netAmountPaid ??
      paymentSummary?.net_amount_paid ??
      amount ??
      "0",
  );
  const remainingRefundableAmount = Math.max(
    0,
    originalPaymentAmount - thisRefundAmount,
  );
  const date =
    paymentData?.result?.extraData?.processedAt ||
    paymentData?.result?.paymentDate ||
    "N/A";
  const refundType =
    paymentData?.result?.extraData?.refundType || "Full Refund";
  const originalReceipt =
    paymentData?.result?.extraData?.originalReceiptNo ||
    paymentData?.result?.paymentLinkedTo?.receiptNo ||
    payment?.referenceReceiptNo ||
    "N/A";
  const originalPaymentId =
    paymentData?.result?.extraData?.originalPaymentId ||
    paymentData?.result?.paymentLinkedTo?.id ||
    payment?.referencePaymentId;
  const paymentMethod =
    paymentData?.result?.extraData?.refundMethod ||
    paymentData?.result?.paymentMode ||
    payment?.paymentModeDisplay ||
    "N/A";
  const approver =
    paymentData?.result?.extraData?.approver ||
    paymentData?.result?.approver ||
    "N/A";
  const reason =
    paymentData?.result?.extraData?.refundReason ||
    paymentData?.result?.reason ||
    "N/A";
  const disposition = paymentData?.result?.extraData?.disposition || "N/A";
  const remarks = paymentData?.result?.extraData?.remarks || "N/A";

  // OVERVIEW
  const overviewFields = [
    { label: "Refund ID", value: refundId },
    {
      label: "Original Receipt",
      value: originalPaymentId ? (
        <button
          onClick={() => navigate(`/cx/payments/view/${originalPaymentId}`)}
          className="text-blue-600 hover:underline font-medium"
        >
          {originalReceipt}
        </button>
      ) : (
        originalReceipt
      ),
    },
    { label: "Processed Date", value: date },
    {
      label: "Processed By",
      value:
        paymentData?.result?.extraData?.processedBy ||
        paymentData?.result?.recordedBy ||
        "N/A",
    },
  ];

  // REFUND DETAILS
  const refundDetailsFields = [
    { label: "Refund Method", value: paymentMethod },
    { label: "Approver", value: approver },
    { label: "Reason", value: reason },
    { label: "Disposition", value: disposition },
    { label: "Remarks", value: remarks },
    {
      label: "Status",
      value: (
        <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-100 border-none font-medium rounded-full px-2.5 py-0.5 text-[11px]">
          {status}
        </Badge>
      ),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-0 shadow-lg">
        {/* Header */}
        <DialogHeader className="border-b px-6 py-4 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-red-500" />
            <DialogTitle className="text-xl font-bold text-gray-800">
              Refund Details
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 space-y-6">
          {/* Hero section */}
          <div className="border border-red-200 bg-red-50/30 rounded-xl p-5 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white border border-red-100 rounded-full flex items-center justify-center shadow-sm">
                <RotateCcw className="text-red-500 w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{name}</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {consumerNo} · {refundId}
                </p>
                <div className="flex gap-2 mt-2">
                  <Badge className="bg-blue-50 text-blue-600 border border-blue-100 font-medium rounded-full">
                    {status}
                  </Badge>
                  <Badge className="bg-red-100 text-red-600 border border-red-200 font-medium rounded-full">
                    {refundType}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-bold text-red-600">
                {currency} {amount}
              </h2>
              <p className="text-xs text-gray-500 mt-1">refunded {date}</p>
            </div>
          </div>

          {/* OVERVIEW */}
          <div>
            <h4 className="text-[12px] font-bold text-gray-500 mb-3 tracking-wider">
              Overview
            </h4>
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              {overviewFields.map((field, index) => (
                <ValueLabel key={index} {...field} />
              ))}
            </div>
          </div>

          {/* REFUND DETAILS */}
          <div>
            <h4 className="text-[12px] font-bold text-gray-500 mb-3 tracking-wider">
              Refund Details
            </h4>
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              {refundDetailsFields.map((field, index) => (
                <ValueLabel key={index} {...field} />
              ))}
            </div>
            {paymentMethod.toLowerCase().includes("bank") && (
              <div className="border border-gray-200 rounded-lg mt-3 bg-white shadow-sm overflow-hidden">
                <button
                  onClick={() =>
                    setIsBankTransferExpanded(!isBankTransferExpanded)
                  }
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 bg-gray-400 rounded-sm" />
                    <span className="font-semibold text-sm text-gray-800">
                      Bank Transfer Details
                    </span>
                  </div>
                  {isBankTransferExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                {isBankTransferExpanded && (
                  <div className="p-3 border-t border-gray-100 text-sm text-gray-600 bg-gray-50">
                    <p>Details not available.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* REFUND SETTLEMENT / AMOUNT BREAKDOWN */}
          <div>
            <h4 className="text-[11px] font-bold text-gray-500 mb-3 tracking-wider">
              Amount Breakdown
            </h4>
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50/50">
                  <tr>
                    <th className="text-left py-2.5 px-4 font-bold text-gray-500 text-[10px] uppercase tracking-wider">
                      Description
                    </th>
                    <th className="text-right py-2.5 px-4 font-bold text-gray-500 text-[10px] uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 px-4 text-gray-600">
                      Original payment amount
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {currency} {originalPaymentAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-600">This refund</td>
                    <td className="py-3 px-4 text-right font-medium text-blue-600">
                      {currency} {thisRefundAmount.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="bg-gray-50/50">
                    <td className="py-3.5 px-4 font-bold text-gray-900">
                      Remaining refundable
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-green-600">
                      {currency} {remainingRefundableAmount.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 flex justify-between bg-gray-50/50 rounded-b-lg items-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-9 px-3 bg-white shadow-sm border-gray-200 text-gray-700"
            >
              <Printer className="w-4 h-4 mr-2 text-gray-500" />
              Print
            </Button>
            <Button
              variant="outline"
              className="h-9 px-3 bg-white shadow-sm border-gray-200 text-gray-700"
            >
              <Download className="w-4 h-4 mr-2 text-gray-500" />
              Download PDF
            </Button>
          </div>
          <div className="flex gap-2">
            {originalPaymentId && (
              <Button
                variant="outline"
                className="h-9 bg-white shadow-sm border-gray-200 text-gray-700"
                onClick={() =>
                  navigate(`/cx/payments/view/${originalPaymentId}`)
                }
              >
                View Original Payment
              </Button>
            )}
            <Button
              className="h-9 bg-blue-600 hover:bg-blue-700 text-white shadow-sm px-6"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
